<script lang="ts" setup>
import { StaticImage } from '@monogrid/vue-boost'
import { gsap } from 'gsap'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useBreakpoint } from '@/composables/useBreakpoint'
import { primaryItems, secondaryItems } from '@/data/menu'
import { states } from '@/store'

import AppFooter from '../layout/AppFooter.vue'
import ModalWrapper from '../molecules/ModalWrapper.vue'

const breakpoints = useBreakpoint()
const route = useRoute()

const open = ref(false)
const currentId = ref<number | string>(-1)
const el = ref<InstanceType<typeof ModalWrapper>>(null)
const tl = gsap.timeline({ paused: true, defaults: { ease: 'power4.out', stagger: 0.15, duration: 0.5 } })
const tlImage = gsap.timeline({ paused: true, defaults: { ease: 'power2.out', duration: 2 } })

const currentImage = computed(() => primaryItems.find(item => item.id === currentId.value)?.image)

const close = () => { states.menu.visible = false }
const updateCurrentId = (id: number | string) => {
  if (currentId.value === -1 || !breakpoints.lg) {
    tlImage.play(0)
  }
  currentId.value = id
}

watch(() => ({ ...route }), (to, from) => {
  if (to.params?.locale !== from?.params?.locale) return
  states.menu.visible = false
})

watch(() => states.menu.visible, (value) => {
  if (value) {
    open.value = true
    tl.timeScale(1).play()
  } else {
    tl.timeScale(4).reverse().eventCallback('onReverseComplete', () => { open.value = false })
  }
  currentId.value = -1
})

watch(() => el.value?.wrapper, (_wrapper) => {
  if (!_wrapper) return

  const footer = gsap.utils.selector(_wrapper)('footer')[0]
  const image = gsap.utils.selector(_wrapper)('figure')[0]
  const mainLinks = gsap.utils.selector(_wrapper)('.primary-item')
  const secondaryLinks = gsap.utils.selector(_wrapper)('.secondary-item')

  tl.clear()
    .fromTo(_wrapper, { yPercent: -100 }, { yPercent: 0, delay: 0.3, duration: 2, ease: 'expo.out' })
    .fromTo(mainLinks, { yPercent: 100 }, { yPercent: 0 }, '-=1.25')
    .fromTo([secondaryLinks, footer], { autoAlpha: 0 }, { autoAlpha: 1, clearProps: 'opacity,visibility' }, '-=0.2')

  tlImage.clear()
    .fromTo(image, { xPercent: 300, rotate: -30, 'transform-origin': 'top left' }, { xPercent: 0, rotate: 0 })
})

onUnmounted(() => {
  tl.kill()
  tlImage.kill()
})
</script>

<template>
  <ModalWrapper ref="el" v-model:visible="open" :fullscreen="true" class="!z-90" @close="states.menu.visible = false">
    <template #close><span class="hidden" /></template>
    <div class="content grid grid-cols-12 items-center px-4 py-25">
      <div class="col-span-12 lg:col-span-5 lg:col-start-2 flex flex-col justify-center gap-12.5">
        <nav>
          <ul class="flex flex-col gap-3">
            <li v-for="item in primaryItems" :key="item.id" class="overflow-hidden">
              <RouterLink
                v-if="!!item.to"
                v-track="{
                  eventCategory: 'Menu',
                  eventAction: 'Click',
                  eventLabel: item.label.en
                }"
                :to="item.to"
                :class="{ '!opacity-100': item.id === currentId }"
                class="primary-item inline-block text-7 line-height-10 lg:text-10 lg:line-height-13"
                @click="close"
                @focus="updateCurrentId(item.id)"
                @pointerenter="updateCurrentId(item.id)"
              >
                {{ item.label[$i18n.locale] }}
              </RouterLink>
              <a
                v-else-if="!!item.externalUrl"
                v-track="{
                  eventCategory: 'Menu',
                  eventAction: 'Click',
                  eventLabel: item.label.en
                }"
                :href="item.externalUrl[$i18n.locale]"
                target="_blank"
                rel="noopener noreferrer"
                :class="{ '!opacity-100': item.id === currentId }"
                class="primary-item inline-block text-7 line-height-10 lg:text-10 lg:line-height-13"
                @focus="updateCurrentId(item.id)"
                @pointerenter="updateCurrentId(item.id)"
              >
                {{ item.label[$i18n.locale] }}
              </a>
            </li>
          </ul>
        </nav>
        <nav>
          <ul>
            <li v-for="item in secondaryItems" :key="item.id" class="overflow-hidden">
              <a
                v-if="!!item.externalUrl"
                v-track="{
                  eventCategory: 'Menu',
                  eventAction: 'Click',
                  eventLabel: item.label.en
                }"
                :href="item.externalUrl[$i18n.locale]"
                target="_blank"
                class="secondary-item"
                rel="noopener noreferrer"
              >
                {{ item.label[$i18n.locale] }}
              </a>
            </li>
          </ul>
        </nav>
      </div>
      <div class="col-span-3 col-start-9 hidden lg:flex items-center justify-center">
        <figure class="relative w-full max-w-150 h-full max-h-150 aspect-ratio-1">
          <Transition mode="in-out">
            <StaticImage
              v-if="currentImage"
              :key="currentImage.src"
              :data="currentImage"
              :cover="true"
              class="!absolute"
            />
          </Transition>
        </figure>
      </div>
    </div>
    <AppFooter class="z-10" />
  </ModalWrapper>
</template>

<style lang="scss" scoped>
.content {
  @screen lt-lg {
    min-height: calc(100% - rem(100px));
  }

  @screen lg {
    min-height: calc(100% - rem(90px));
  }
}

a {
  @include hover {
    opacity: 0.4;
    transition: opacity $default-duration;

    &:hover {
      opacity: 1;
    }
  }
}
</style>
